import React, { useState, useEffect, useRef } from 'react';
import {
  Globe, Moon, Sun, ArrowRight, Activity, Zap, Layers, RefreshCw,
  ShieldCheck, Server, MonitorPlay, Cpu, Database, Network, ChevronRight,
  Settings, CheckCircle2, AlertCircle, PlayCircle, Lock, Sparkles, Boxes,
  Compass, TrendingUp, Check, X, MessageSquare, FileText, FileSearch
} from 'lucide-react';
import { postWorkflowAssessment, postContact } from '@/services/api';
import BpmnViewer from '@/components/BpmnViewer';
import { TimelineContent } from '@/components/ui/timeline-animation';
import { InteractiveImageAccordion } from '@/components/ui/interactive-image-accordion';
import { AuroraBackground } from '@/components/ui/aurora-background';
import { RoadmapCard } from '@/components/ui/roadmap-card';
import { CardCarousel } from '@/components/ui/card-carousel';
import { EyebrowLabel } from '@/components/ui/EyebrowLabel';
import { QuoteBlock } from '@/components/ui/QuoteBlock';
import type { AssessmentResponse } from '@/types/assessment';

// Which branch a lead originated from — folded into the ERPNext Lead `source`
// so sales can tell Enterprise and Growth apart. Default is 'enterprise'.
type Branch = 'enterprise' | 'growth';

// --- TRANSLATIONS ---
const translations = {
  en: {
    nav: {
      services: "The problem",
      enterprise: "Enterprise",
      ai: "AI",
      mapper: "Mapper",
      approach: "Approach",
      growth: "Growth",
      about: "About",
      tech: "Technologies",
      contact: "Contact"
    },
    hero: {
      badge: "Software · Automation · AI for your operation",
      headline: "We modernize operations that matter",
      // Substring of `headline` to gradient-highlight. Must appear exactly once.
      headlineAccent: "operations that matter",
      subhead: "We design and build the software, automation, and AI that make complex operations run better — from organizations running SAP to growing companies that want to scale on solid foundations. Senior-led, delivered in weeks.",
      ctaPrimary: "Let's talk",
      ctaSecondary: "How We Work",
      pillarsLabel: "Where we go to work",
      pillars: [
        {
          id: "process",
          title: "Process",
          short: "Map, redesign, modernize the workflow itself.",
          long: "We start with the business process — not the software. Workflow mapping, BPMN modelling, hand-off analysis, and a target-state design that reduces handover time and removes hidden tribal knowledge.",
          bullets: [
            "Workflow mapping & BPMN models",
            "Hand-off and approval-step audit",
            "Target-state design with measurable KPIs",
            "Change-management plan you can actually run",
          ],
          imgAlt: "Whiteboard with process diagrams and sticky notes",
        },
        {
          id: "software",
          title: "Software",
          short: "Systems that fit how your business actually runs.",
          long: "We build the missing pieces: modern UIs, custom modules where off-the-shelf tools fall short, and integration layers that connect what you already use — including SAP (OData, ABAP RAP, BTP) when that's part of the picture.",
          bullets: [
            "Modern web UIs decoupled from legacy backends",
            "ERPNext + Frappe for teams without SAP",
            "SAP integration via OData, RAP, BTP when needed",
            "Custom modules where standard tools have gaps",
          ],
          imgAlt: "Developer workstation with code on screen",
        },
        {
          id: "automation",
          title: "Automation",
          short: "Replace manual steps with reliable, observable automation.",
          long: "We use Flowable for orchestration, n8n for peripheral integrations, and direct APIs where the latency matters. Every automation comes with audit trails, retry logic, and a dashboard you can actually read.",
          bullets: [
            "Flowable BPMN engine for orchestration",
            "n8n for tactical integrations",
            "Audit trail + retry logic by default",
            "Operations dashboards (Grafana, custom)",
          ],
          imgAlt: "Robotic arm in a manufacturing line",
        },
        {
          id: "ai",
          title: "AI",
          short: "Practical AI that augments operators, not replaces them.",
          long: "Document extraction, voice-to-process flows, decision support, and embedded copilots tailored to your data. We pick the model — Gemini, Claude, or local — based on cost, latency and security, not hype.",
          bullets: [
            "OCR & structured data extraction",
            "Voice-to-action operator interfaces",
            "RAG over enterprise documents",
            "Embedded copilots in business apps",
          ],
          imgAlt: "Abstract neural network visualization",
        },
      ],
      modalClose: "Close",
      modalCta: "Start a Workflow Assessment",
    },
    contactModal: {
      title: "Let's talk",
      subtitle: "Tell us a bit about your business and we'll get back to you within one business day.",
      name: "Full Name",
      company: "Company",
      email: "Work Email",
      phone: "Phone (optional)",
      message: "How can we help? (optional)",
      messagePh: "A sentence or two about what you're trying to solve.",
      consent: "I agree to be contacted about my inquiry.",
      submit: "Send",
      sending: "Sending…",
      close: "Close",
      successTitle: "Thank you!",
      successMessage: "We've received your message and will be in touch within one business day.",
      errorGeneric: "Something went wrong. Please try again.",
    },
    problem: {
      headline: "Growing operations outrun the tools that run them",
      cards: [
        { title: "Work Happens in the Cracks", desc: "Critical steps live in spreadsheets, email, and side tools — so no system ever sees the full picture." },
        { title: "Screens People Avoid", desc: "Outdated interfaces slow people down and push them back to manual workarounds." },
        { title: "No Clear Trail", desc: "No one can see where a request is stuck or who approved what, until something breaks." },
        { title: "Change Takes Months", desc: "Adapting a rigid legacy tool to a new business rule turns into a project every time." },
        { title: "It Only Works Because Someone Remembers", desc: "The process depends on one person's know-how, not a system anyone can run." }
      ]
    },
    solution: {
      eyebrow: "The solution",
      headline: "Depending on where you start, we work in three ways.",
      tiles: [
        { label: "Enterprise", when: "Your operations run around SAP", anchor: "#enterprise", accent: "enterprise" as const },
        { label: "Growth", when: "You need to organize operations from scratch", anchor: "#growth", accent: "growth" as const },
        { label: "AI for everyone", when: "Capabilities we apply across both", anchor: "#ai", accent: "neutral" as const },
      ],
    },
    enterprise: {
      eyebrow: "Episteck Enterprise",
      headline: "Three ways we modernize around SAP",
      subtitle: "Senior-led engagements for medium and large organizations. Start with an assessment, then build only what earns its place.",
      cards: [
        {
          pain: "I have broken processes but don't know what to modernize first or what it costs.",
          title: "Consulting & Modernization Assessment",
          desc: "We map the current state, design the target state, and hand you a phased plan you can act on.",
          bullets: [
            "AS-IS / TO-BE workflow mapping",
            "Target-state architecture & KPIs",
            "Phased, low-risk modernization plan",
            "Fixed-scope, no lock-in"
          ]
        },
        {
          pain: "I know what needs to be done. I need someone to build it and connect it to SAP.",
          title: "Automation & AI Projects",
          desc: "Reliable orchestration and practical AI, connected to SAP — with humans in control of every decision that matters.",
          bullets: [
            "Flowable BPMN orchestration",
            "Modern interfaces on S/4 and ECC",
            "AI document extraction",
            "SAP integration via OData and BTP"
          ]
        },
        {
          pain: "I need senior SAP engineering: ABAP, BTP, upgrade, Joule.",
          title: "SAP Projects",
          desc: "Hands-on SAP engineering across S/4HANA and ECC — from custom development to platform work and upgrades.",
          bullets: [
            "S/4HANA & ECC development",
            "SAP BTP apps & integrations",
            "Joule and SAP AI adoption",
            "Migrations & version upgrades"
          ]
        }
      ],
      pontis: {
        label: "Episteck IP",
        name: "Pontis",
        desc: "Our own toolkit that gives AI agents secure, audited access to SAP ABAP systems. Consultant's own SAP credentials. Human confirmation on every change. Tamper-evident audit log.",
      },
      cta: "Tell us about your process →",
      ctaNote: "Free initial assessment · Response within 48h",
    },
    aiSection: {
      eyebrow: "AI for everyone",
      headline: "Practical AI for enterprise operations",
      subtitle: "AI should assist your teams, not replace them. We build it into the workflow with people kept in control of every decision that matters.",
      capabilities: [
        { title: "Understand natural-language requests", desc: "Operators describe what they need in plain language instead of navigating rigid menus." },
        { title: "Extract information from documents", desc: "Pull structured data from invoices, forms, and PDFs so it doesn't have to be re-typed." },
        { title: "Summarize and explain processes", desc: "Turn a tangle of steps and systems into a clear summary anyone can follow." },
        { title: "Suggest the next action", desc: "Surface the likely next step and flag workflow bottlenecks before they stall." },
        { title: "Reduce repetitive manual work", desc: "Automate the copy-paste-between-systems steps that eat operator time." },
        { title: "Support approvals, humans in control", desc: "AI prepares and recommends; a person approves. Every action is logged and reversible." }
      ],
      toolkitLabel: "Our SAP toolkit",
      toolkitTitle: "Pontis — secure AI access to ABAP systems",
      toolkitDesc: "Pontis is our own toolkit that lets AI coding agents work safely inside SAP ABAP systems: it runs on the consultant's machine, uses their own SAP credentials, gates every change behind a human confirmation, and keeps a tamper-evident audit log. AI accelerates the work; SAP authorizations and a person stay in charge."
    },
    mapper: {
      headline: "Map your workflow in 2 minutes",
      subtitle: "Describe a legacy process and receive a first modernization map with automation opportunities.",
      step1: {
        title: "Step 1: Describe the Workflow",
        processName: "Process Name",
        processNamePh: "e.g., Vendor Onboarding",
        description: "Short Description",
        descriptionPh: "What does this process do?",
        trigger: "Who or what starts the process?",
        triggerPh: "e.g., Email received",
        roles: "Main Participants / Roles",
        rolesPh: "e.g., HR, Manager",
        approvals: "Approval Steps",
        approvalsPh: "e.g., 2-step approval",
        systems: "Systems Involved",
        systemsPh: "e.g., Salesforce, Excel",
        sapRole: "Where does SAP fit?",
        sapRolePh: "e.g., Final record creation",
        painPoints: "Current Pain Points",
        painPointsPh: "What is broken?",
        outcome: "Desired Outcome",
        outcomePh: "What is the goal?",
        generateBtn: "Generate Workflow Map"
      },
      loading: [
        "Analyzing legacy process structure...",
        "Identifying SAP touchpoints...",
        "Mapping automation opportunities...",
        "Generating modernization blueprint..."
      ],
      preview: {
        title: "First Modernization Map",
        disclaimer: "Assessment Preview. Not for production use.",
        summaryTitle: "Modernization Summary",
        opportunitiesTitle: "Automation Opportunities",
        automation1: "Replace manual data entry with API integration.",
        automation2: "Automate SAP master data creation via BTP.",
        automation3: "Consolidate approvals into a unified modern inbox.",
        nextStepBtn: "Send to Episteck for Full Assessment"
      },
      lead: {
        title: "Send your workflow to Episteck",
        subtitle: "Our senior architects will review your modernization map and contact you with targeted recommendations.",
        name: "Full Name",
        company: "Company",
        email: "Work Email",
        phone: "Phone (Optional)",
        consent: "I agree to be contacted regarding this workflow assessment.",
        submitBtn: "Send My Workflow"
      },
      success: {
        title: "Workflow Received",
        message: "Your modernization map has been sent to our architecture team. We will review the SAP touchpoints and automation opportunities and reach out shortly."
      }
    },
    approach: {
      headline: "A low-risk path from legacy to modern automation",
      phases: [
        { title: "Discover", desc: "Analyze current state, pain points, and SAP landscape." },
        { title: "Blueprint", desc: "Design modern architecture and BPMN conceptual models." },
        { title: "Validate", desc: "Prototype interfaces and SAP integrations to reduce risk." },
        { title: "Implement", desc: "Accelerated engineering utilizing Flowable and enterprise APIs." },
        { title: "Stabilize", desc: "Rollout, monitoring, and handover to your operational teams." }
      ]
    },
    whyEpisteck: {
      headline: "Specialized consultancy. Enterprise mindset.",
      cards: [
        { title: "Workflow-First, Not Code-First", desc: "We design around the business process and the data it produces — not just a new screen on top of the old problem." },
        { title: "Senior SAP Understanding", desc: "Deep, hands-on expertise in SAP data models, OData, ABAP RAP, and BTP — so integrations fit how SAP actually works." },
        { title: "Accelerated, Senior-Led Delivery", desc: "Small teams of experienced engineers deliver in rapid cycles, without the overhead of a large consultancy." },
        { title: "Start Small, No Lock-In", desc: "Begin with a fixed-scope assessment. If it's not worth continuing, you stop — no long contract, no heavy footprint left behind." }
      ]
    },
    examples: {
      headline: "What modernization looks like",
      subtitle: "Representative examples of the kind of transformation we do. Illustrative, not specific client cases.",
      disclaimer: "Illustrative example",
      labels: { problem: "Problem", solution: "Solution", result: "Result" },
      cases: [
        {
          name: "Purchase Approval",
          problem: "Approvals bounce around by email and spreadsheet; no one knows where a request is stuck, and the PO is keyed into SAP by hand at the end.",
          solution: "A single approval interface with the routing rules built in, writing the purchase order straight to SAP once approved.",
          result: "A clear, auditable trail from request to PO, and no manual re-keying into SAP."
        },
        {
          name: "Customer Onboarding",
          problem: "A new customer's data is collected in forms and re-typed into several systems, so records disagree and setup drags on.",
          solution: "One guided intake that validates the data once and creates the customer master in SAP, with the other systems fed from the same source.",
          result: "Consistent master data and a faster, less error-prone onboarding."
        },
        {
          name: "Quality Incident",
          problem: "Incidents are logged in notebooks and chat; nothing links back to SAP, so follow-up and reporting depend on who remembers what.",
          solution: "A structured incident workflow with photo capture and status tracking, connected to the relevant SAP records.",
          result: "Every incident traceable end to end, with reporting that reflects reality."
        }
      ]
    },
    about: {
      headline: "What is Episteck?",
      lead: "Episteck is a specialized consultancy that modernizes enterprise workflows around SAP.",
      paragraphs: [
        "We help organizations replace legacy workflow tools and manual, spreadsheet-and-email processes with modern automation and SAP-connected interfaces — without replacing SAP itself.",
        "We work process-first: we map how work actually flows, redesign it around clear ownership and reliable data, and build the missing pieces — modern UIs, integrations, and automation — on top of your existing SAP landscape.",
        "Engagements are senior-led and delivered in accelerated cycles. Most start with a focused Workflow Modernization Assessment before any build."
      ],
      forWhoLabel: "Who we work with",
      forWho: "Medium and large organizations — especially those running SAP — with legacy applications, fragmented systems, low process traceability, and a need for automation that respects their SAP core."
    },
    growth: {
      eyebrow: "Episteck Growth",
      headline: "Modern operations for growing businesses",
      quote: "One system to run the business — and automation added exactly when the business is ready for it.",
      intro: "Not every company runs SAP. For growing businesses, we implement ERPNext to bring sales, inventory, accounting, and operations into one place — then automate the steps that slow the team down.",
      fitLabel: "A good fit if you",
      fit: [
        "Have grown past spreadsheets and disconnected tools",
        "Run a team of roughly 10–100 people",
        "Are a distributor, service business, or small manufacturer",
        "Have information scattered across apps, chats, and files"
      ],
      plansLabel: "Choose your plan",
      featuredBadge: "★ Most popular",
      plans: [
        { id: "start", name: "Start", target: "Professional services · up to 15 users", setup: "From $X", monthly: "+ $X/mo", features: ["CRM and clients", "Quotes and sales", "Purchasing", "Projects", "Hosting + support"], featured: false, caseStudy: null },
        { id: "operations", name: "Operations", target: "Distributors · retail · inventory", setup: "From $X", monthly: "+ $X/mo", features: ["Everything in Start", "Inventory and warehouses", "Deliveries and dispatch", "Operational dashboards", "Approvals"], featured: true, caseStudy: null },
        { id: "manufacturing", name: "Manufacturing", target: "Small manufacturers · processors", setup: "From $X", monthly: "+ $X/mo", features: ["Everything in Operations", "BOM and production orders", "Manufacturing costs", "Quality control", "Dedicated support"], featured: false, caseStudy: null },
        { id: "agricultura", name: "Agricultura", target: "Farms · agro-exporters · cooperatives", setup: "From $X", monthly: "+ $X/mo", features: ["Harvests and lots", "Quality control", "Field expenses", "Role-based dashboards", "Support in Spanish"], featured: false, caseStudy: { label: "Real case", text: "Imox Natural — organic pimentón farm, Lara VE. ERPNext live with role dashboards." } },
      ],
      plansCta: "See live demo →",
      plansCtaNote: "Setup in 2 weeks · No long contract · Free discovery session",
      plansFootnote: "Prices in USD. To be confirmed with your account manager before go-live.",
      cta: "Explore Episteck Growth"
    },
    tech: {
      headline: "Platforms we work with",
      subtitle: "The enterprise platforms we build on.",
      cards: [
        "SAP", "ABAP", "SAP BTP", "SAP Build Process Automation",
        "OData", "ABAP RAP", "Flowable", "n8n",
        "React", "TypeScript", "Python", "FastAPI",
        "Anthropic Claude", "OpenAI", "Google Gemini",
        "PostgreSQL", "Docker", "REST APIs",
        "Pontis (our SAP AI toolkit)"
      ]
    },
    lab: {
      headline: "From concept to working prototype",
      cards: [
        { title: "Legacy Workflow Replacement", type: "Process Demo", img: "/Episteck_Service_Visuals/visual-workflow-modernization.svg" },
        { title: "SAP-Connected Approval Flow", type: "Integration Demo", img: "/Episteck_Service_Visuals/visual-seamless-integration.svg" },
        { title: "AI-Assisted Workflow Delivery", type: "Engineering Demo", img: "/Episteck_Service_Visuals/visual-process-orchestration.svg" }
      ]
    },
    assessment: {
      headline: "Start with a Workflow Modernization Assessment",
      items: [
        "Current-state workflow review", "Modernization opportunities",
        "Target architecture", "Automation roadmap", "Risk assessment", "Estimated implementation plan"
      ],
      btn: "Request Assessment"
    },
    footerCTA: {
      headline: "Planning to replace a legacy workflow around SAP?",
      btn: "Talk to Episteck",
      copyright: "© 2026 Episteck. Specialized Workflow Modernization."
    }
  },
  es: {
    nav: {
      services: "El problema",
      enterprise: "Enterprise",
      ai: "IA",
      mapper: "Mapeador",
      approach: "Enfoque",
      growth: "Growth",
      about: "Nosotros",
      tech: "Tecnologías",
      contact: "Contacto"
    },
    hero: {
      badge: "Software · Automatización · IA para tu operación",
      headline: "Modernizamos operaciones críticas",
      // Substring de `headline` a resaltar con degradado. Debe aparecer exactamente una vez.
      headlineAccent: "operaciones críticas",
      subhead: "Diseñamos y construimos el software, la automatización y la IA que hacen que operaciones complejas funcionen mejor — desde organizaciones que corren SAP hasta empresas en crecimiento que quieren escalar sobre bases sólidas. Liderado por seniors, entregado en semanas.",
      ctaPrimary: "Hablemos",
      ctaSecondary: "Cómo Trabajamos",
      pillarsLabel: "Dónde trabajamos",
      pillars: [
        {
          id: "process",
          title: "Procesos",
          short: "Mapear, rediseñar y modernizar el flujo de trabajo.",
          long: "Empezamos por el proceso de negocio — no por el software. Mapeo de flujos, modelado BPMN, análisis de traspasos y un diseño de estado objetivo que reduce el tiempo de transición y elimina el conocimiento tribal oculto.",
          bullets: [
            "Mapeo de flujos y modelos BPMN",
            "Auditoría de traspasos y aprobaciones",
            "Diseño de estado objetivo con KPIs medibles",
            "Plan de gestión del cambio ejecutable",
          ],
          imgAlt: "Pizarra con diagramas de proceso y notas adhesivas",
        },
        {
          id: "software",
          title: "Software",
          short: "Sistemas que se adaptan a cómo opera tu negocio.",
          long: "Construimos las piezas que faltan: UIs modernas, módulos custom donde las herramientas estándar no llegan, y capas de integración que conectan lo que ya usas — incluyendo SAP (OData, ABAP RAP, BTP) cuando es parte del panorama.",
          bullets: [
            "UIs web modernas desacopladas del backend heredado",
            "ERPNext + Frappe para equipos sin SAP",
            "Integración SAP vía OData, RAP, BTP cuando hace falta",
            "Módulos custom donde las herramientas estándar tienen huecos",
          ],
          imgAlt: "Estación de trabajo con código en pantalla",
        },
        {
          id: "automation",
          title: "Automatización",
          short: "Reemplazar pasos manuales con automatización confiable y observable.",
          long: "Usamos Flowable para orquestación, n8n para integraciones periféricas y APIs directas donde la latencia importa. Cada automatización viene con trazabilidad, lógica de reintentos y un dashboard que se puede leer.",
          bullets: [
            "Motor BPMN Flowable para orquestación",
            "n8n para integraciones tácticas",
            "Trazabilidad y reintentos por defecto",
            "Dashboards de operación (Grafana, custom)",
          ],
          imgAlt: "Brazo robótico en una línea de manufactura",
        },
        {
          id: "ai",
          title: "IA",
          short: "IA práctica que asiste al operador, no lo reemplaza.",
          long: "Extracción de documentos, flujos de voz a proceso, soporte de decisiones y copilotos embebidos sobre sus datos. Elegimos el modelo — Gemini, Claude o local — según costo, latencia y seguridad, no según moda.",
          bullets: [
            "OCR y extracción de datos estructurados",
            "Interfaces de voz a acción para operadores",
            "RAG sobre documentos empresariales",
            "Copilotos embebidos en aplicaciones de negocio",
          ],
          imgAlt: "Visualización abstracta de red neuronal",
        },
      ],
      modalClose: "Cerrar",
      modalCta: "Iniciar Evaluación de Flujo",
    },
    contactModal: {
      title: "Hablemos",
      subtitle: "Cuéntanos un poco sobre tu negocio y te respondemos en un día hábil.",
      name: "Nombre Completo",
      company: "Empresa",
      email: "Correo de Trabajo",
      phone: "Teléfono (opcional)",
      message: "¿En qué podemos ayudarte? (opcional)",
      messagePh: "Una o dos frases sobre lo que buscas resolver.",
      consent: "Acepto ser contactado con respecto a mi consulta.",
      submit: "Enviar",
      sending: "Enviando…",
      close: "Cerrar",
      successTitle: "¡Gracias!",
      successMessage: "Recibimos tu mensaje y te contactaremos en un día hábil.",
      errorGeneric: "Algo salió mal. Por favor intenta de nuevo.",
    },
    problem: {
      headline: "Las operaciones crecen más rápido que las herramientas que las sostienen",
      cards: [
        { title: "El trabajo ocurre en los huecos", desc: "Pasos críticos viven en hojas de cálculo, correos y herramientas aparte — así que ningún sistema ve el panorama completo." },
        { title: "Pantallas que la gente evita", desc: "Interfaces obsoletas frenan al equipo y lo empujan de vuelta a soluciones manuales." },
        { title: "Sin rastro claro", desc: "Nadie ve dónde se atascó una solicitud ni quién aprobó qué, hasta que algo se rompe." },
        { title: "Cada cambio toma meses", desc: "Adaptar una herramienta heredada y rígida a una nueva regla de negocio se convierte en un proyecto cada vez." },
        { title: "Solo funciona porque alguien se acuerda", desc: "El proceso depende del conocimiento de una persona, no de un sistema que cualquiera pueda operar." }
      ]
    },
    solution: {
      eyebrow: "La solución",
      headline: "Dependiendo de tu punto de partida, trabajamos de tres formas.",
      tiles: [
        { label: "Enterprise", when: "Tu operación gira alrededor de SAP", anchor: "#enterprise", accent: "enterprise" as const },
        { label: "Growth", when: "Necesitas organizar tu operación desde cero", anchor: "#growth", accent: "growth" as const },
        { label: "IA para todos", when: "Capacidades que aplicamos en los dos", anchor: "#ai", accent: "neutral" as const },
      ],
    },
    enterprise: {
      eyebrow: "Episteck Enterprise",
      headline: "Tres formas de modernizar en torno a SAP",
      subtitle: "Proyectos liderados por seniors para organizaciones medianas y grandes. Comience con una evaluación y construya solo lo que vale la pena.",
      cards: [
        {
          pain: "Tengo procesos rotos pero no sé qué modernizar primero ni cuánto cuesta.",
          title: "Consultoría y Evaluación de Modernización",
          desc: "Mapeamos el estado actual, diseñamos el estado objetivo y le entregamos un plan por fases sobre el que puede actuar.",
          bullets: [
            "Mapeo de flujos AS-IS / TO-BE",
            "Arquitectura objetivo y KPIs",
            "Plan de modernización por fases y bajo riesgo",
            "Alcance fijo, sin ataduras"
          ]
        },
        {
          pain: "Sé qué hay que hacer. Necesito quien lo construya y lo conecte a SAP.",
          title: "Proyectos de Automatización e IA",
          desc: "Orquestación confiable e IA práctica, conectadas a SAP — con las personas al control de cada decisión que importa.",
          bullets: [
            "Orquestación BPMN con Flowable",
            "Interfaces modernas sobre S/4 y ECC",
            "Extracción de documentos con IA",
            "Integración SAP vía OData y BTP"
          ]
        },
        {
          pain: "Necesito ingeniería SAP senior: ABAP, BTP, upgrade, Joule.",
          title: "Proyectos SAP",
          desc: "Ingeniería SAP práctica sobre S/4HANA y ECC — desde desarrollo custom hasta trabajo de plataforma y upgrades.",
          bullets: [
            "Desarrollo en S/4HANA y ECC",
            "Apps e integraciones en SAP BTP",
            "Adopción de Joule e IA de SAP",
            "Migraciones y upgrades de versión"
          ]
        }
      ],
      pontis: {
        label: "IP Episteck",
        name: "Pontis",
        desc: "Nuestro toolkit propio que da a agentes de IA acceso seguro y auditado a sistemas SAP ABAP. Credenciales SAP del consultor. Confirmación humana en cada cambio. Audit log inmutable.",
      },
      cta: "Cuéntanos tu proceso →",
      ctaNote: "Evaluación inicial sin costo · Respuesta en 48h",
    },
    aiSection: {
      eyebrow: "IA para todos",
      headline: "IA práctica para operaciones empresariales",
      subtitle: "La IA debe asistir a sus equipos, no reemplazarlos. La integramos en el flujo de trabajo con las personas al control de cada decisión que importa.",
      capabilities: [
        { title: "Entender solicitudes en lenguaje natural", desc: "Los operadores describen lo que necesitan en lenguaje sencillo, sin navegar menús rígidos." },
        { title: "Extraer información de documentos", desc: "Obtiene datos estructurados de facturas, formularios y PDFs para no tener que re-digitarlos." },
        { title: "Resumir y explicar procesos", desc: "Convierte una maraña de pasos y sistemas en un resumen claro que cualquiera puede seguir." },
        { title: "Sugerir la siguiente acción", desc: "Propone el siguiente paso probable y señala cuellos de botella antes de que se atasquen." },
        { title: "Reducir el trabajo manual repetitivo", desc: "Automatiza los pasos de copiar-y-pegar entre sistemas que consumen el tiempo del operador." },
        { title: "Apoyar aprobaciones, con humanos al control", desc: "La IA prepara y recomienda; una persona aprueba. Cada acción queda registrada y es reversible." }
      ],
      toolkitLabel: "Nuestro toolkit SAP",
      toolkitTitle: "Pontis — acceso seguro de IA a sistemas ABAP",
      toolkitDesc: "Pontis es nuestro propio toolkit que permite a los agentes de IA de programación trabajar de forma segura dentro de sistemas SAP ABAP: corre en la máquina del consultor, usa sus propias credenciales SAP, exige confirmación humana en cada cambio y mantiene un registro de auditoría a prueba de manipulación. La IA acelera el trabajo; las autorizaciones SAP y una persona siguen al mando."
    },
    mapper: {
      headline: "Mapee su flujo en 2 minutos",
      subtitle: "Describa un proceso heredado y reciba un primer mapa de modernización con oportunidades de automatización.",
      step1: {
        title: "Paso 1: Describir el Flujo",
        processName: "Nombre del Proceso",
        processNamePh: "Ej: Alta de Proveedores",
        description: "Breve Descripción",
        descriptionPh: "¿Qué hace este proceso?",
        trigger: "¿Quién o qué inicia el proceso?",
        triggerPh: "Ej.: Recepción de correo",
        roles: "Participantes / Roles",
        rolesPh: "Ej.: RRHH, Gerente",
        approvals: "Pasos de Aprobación",
        approvalsPh: "Ej.: Aprobación en dos niveles",
        systems: "Sistemas Involucrados",
        systemsPh: "Ej.: Salesforce, Excel",
        sapRole: "¿Dónde encaja SAP?",
        sapRolePh: "Ej.: Creación del registro final",
        painPoints: "Puntos de Dolor Actuales",
        painPointsPh: "¿Qué está fallando?",
        outcome: "Resultado Deseado",
        outcomePh: "¿Cuál es el objetivo?",
        generateBtn: "Generar Mapa de Flujo"
      },
      loading: [
        "Analizando estructura del proceso heredado...",
        "Identificando puntos de contacto SAP...",
        "Mapeando oportunidades de automatización...",
        "Generando plano de modernización..."
      ],
      preview: {
        title: "Primer Mapa de Modernización",
        disclaimer: "Vista previa de evaluación. No apto para producción.",
        summaryTitle: "Resumen de Modernización",
        opportunitiesTitle: "Oportunidades de Automatización",
        automation1: "Reemplazar entrada manual con integración API.",
        automation2: "Automatizar creación de datos maestros SAP vía BTP.",
        automation3: "Consolidar aprobaciones en una bandeja de entrada unificada.",
        nextStepBtn: "Enviar a Episteck para Evaluación Completa"
      },
      lead: {
        title: "Envíe su flujo a Episteck",
        subtitle: "Nuestros arquitectos revisarán su mapa y le contactarán con recomendaciones.",
        name: "Nombre Completo",
        company: "Empresa",
        email: "Correo de Trabajo",
        phone: "Teléfono (Opcional)",
        consent: "Acepto ser contactado con respecto a esta evaluación.",
        submitBtn: "Enviar Mi Flujo"
      },
      success: {
        title: "Flujo Recibido",
        message: "Su mapa ha sido enviado a nuestro equipo de arquitectura. Revisaremos los puntos SAP y le contactaremos en breve."
      }
    },
    approach: {
      headline: "Un camino de bajo riesgo hacia la automatización",
      phases: [
        { title: "Descubrimiento", desc: "Análisis del estado actual, dolores y entorno SAP." },
        { title: "Blueprint", desc: "Diseño de arquitectura moderna y modelos conceptuales BPMN." },
        { title: "Validación", desc: "Prototipado de interfaces e integraciones SAP para reducir riesgos." },
        { title: "Implementación", desc: "Ingeniería acelerada utilizando Flowable y APIs empresariales." },
        { title: "Estabilización", desc: "Despliegue, monitoreo y traspaso a sus equipos operativos." }
      ]
    },
    whyEpisteck: {
      headline: "Consultora especializada. Mentalidad Enterprise.",
      cards: [
        { title: "Primero el flujo, no el código", desc: "Diseñamos en torno al proceso de negocio y al dato que produce — no solo una pantalla nueva sobre el viejo problema." },
        { title: "Comprensión Senior de SAP", desc: "Experiencia práctica y profunda en modelos de datos SAP, OData, ABAP RAP y BTP — para que las integraciones encajen con cómo funciona SAP de verdad." },
        { title: "Entrega acelerada y senior", desc: "Equipos pequeños de ingenieros experimentados entregan en ciclos rápidos, sin la carga de una gran consultora." },
        { title: "Empiece pequeño, sin ataduras", desc: "Arranque con una evaluación de alcance fijo. Si no vale la pena continuar, se detiene — sin contrato largo ni huella pesada que quede detrás." }
      ]
    },
    examples: {
      headline: "Cómo se ve la modernización",
      subtitle: "Ejemplos representativos del tipo de transformación que hacemos. Ilustrativos, no casos específicos de clientes.",
      disclaimer: "Ejemplo ilustrativo",
      labels: { problem: "Problema", solution: "Solución", result: "Resultado" },
      cases: [
        {
          name: "Aprobación de Compras",
          problem: "Las aprobaciones van y vienen por correo y hoja de cálculo; nadie sabe dónde se atascó una solicitud, y la orden de compra se digita en SAP a mano al final.",
          solution: "Una sola interfaz de aprobación con las reglas de ruteo incorporadas, que escribe la orden de compra directo en SAP una vez aprobada.",
          result: "Un rastro claro y auditable desde la solicitud hasta la OC, sin re-digitación manual en SAP."
        },
        {
          name: "Alta de Clientes",
          problem: "Los datos de un cliente nuevo se recogen en formularios y se re-digitan en varios sistemas, así que los registros no coinciden y el alta se demora.",
          solution: "Un ingreso guiado que valida el dato una sola vez y crea el maestro de cliente en SAP, alimentando los demás sistemas desde la misma fuente.",
          result: "Datos maestros consistentes y un alta más rápida y con menos errores."
        },
        {
          name: "Incidente de Calidad",
          problem: "Los incidentes se registran en cuadernos y chat; nada enlaza con SAP, así que el seguimiento y el reporte dependen de quién recuerde qué.",
          solution: "Un flujo estructurado de incidentes con captura de fotos y seguimiento de estado, conectado a los registros SAP relevantes.",
          result: "Cada incidente trazable de extremo a extremo, con reportes que reflejan la realidad."
        }
      ]
    },
    about: {
      headline: "¿Qué es Episteck?",
      lead: "Episteck es una consultora especializada en modernizar los flujos de trabajo empresariales alrededor de SAP.",
      paragraphs: [
        "Ayudamos a las organizaciones a reemplazar herramientas heredadas y procesos manuales de hojas de cálculo y correos por automatización moderna e interfaces conectadas a SAP — sin reemplazar SAP.",
        "Trabajamos con el proceso primero: mapeamos cómo fluye realmente el trabajo, lo rediseñamos en torno a responsabilidades claras y datos confiables, y construimos las piezas que faltan — UIs modernas, integraciones y automatización — sobre su entorno SAP existente.",
        "Los proyectos son liderados por seniors y se entregan en ciclos acelerados. La mayoría comienza con una Evaluación de Modernización de Flujo acotada antes de construir nada."
      ],
      forWhoLabel: "Con quién trabajamos",
      forWho: "Organizaciones medianas y grandes — especialmente con SAP — con aplicaciones heredadas, sistemas fragmentados, baja trazabilidad de procesos y necesidad de automatización que respete su núcleo SAP."
    },
    growth: {
      eyebrow: "Episteck Growth",
      headline: "Operaciones modernas para empresas en crecimiento",
      quote: "Un solo sistema para operar el negocio — y automatización agregada justo cuando el negocio está listo para ella.",
      intro: "No toda empresa usa SAP. Para negocios en crecimiento implementamos ERPNext para unir ventas, inventario, contabilidad y operaciones en un solo lugar — y luego automatizamos los pasos que frenan al equipo.",
      fitLabel: "Encaja bien si usted",
      fit: [
        "Ya superó las hojas de cálculo y las herramientas desconectadas",
        "Tiene un equipo de aproximadamente 10 a 100 personas",
        "Es distribuidor, empresa de servicios o pequeño fabricante",
        "Tiene información dispersa entre apps, chats y archivos"
      ],
      plansLabel: "Elige tu plan",
      featuredBadge: "★ Más popular",
      plans: [
        { id: "start", name: "Start", target: "Servicios profesionales · hasta 15 usuarios", setup: "Desde $X", monthly: "+ $X/mes", features: ["CRM y clientes", "Cotizaciones y ventas", "Compras", "Proyectos", "Hosting + soporte"], featured: false, caseStudy: null },
        { id: "operations", name: "Operations", target: "Distribuidoras · comercios · inventario", setup: "Desde $X", monthly: "+ $X/mes", features: ["Todo en Start", "Inventario y almacenes", "Entregas y despacho", "Dashboards operativos", "Aprobaciones"], featured: true, caseStudy: null },
        { id: "manufacturing", name: "Manufacturing", target: "Pequeños fabricantes · procesadores", setup: "Desde $X", monthly: "+ $X/mes", features: ["Todo en Operations", "BOM y órdenes de producción", "Costos de manufactura", "Control de calidad", "Soporte dedicado"], featured: false, caseStudy: null },
        { id: "agricultura", name: "Agricultura", target: "Fincas · agroexportadores · cooperativas", setup: "Desde $X", monthly: "+ $X/mes", features: ["Cosechas y lotes", "Control de calidad", "Gastos de campo", "Dashboards por rol", "Soporte en español"], featured: false, caseStudy: { label: "Caso real", text: "Imox Natural — finca orgánica de pimentón, Lara VE. ERPNext live con dashboards por rol." } },
      ],
      plansCta: "Ver demo en vivo →",
      plansCtaNote: "Setup en 2 semanas · Sin contrato largo · Discovery inicial sin costo",
      plansFootnote: "Precios en USD. A confirmar con tu ejecutivo antes del go-live.",
      cta: "Conocer Episteck Growth"
    },
    tech: {
      headline: "Plataformas con las que trabajamos",
      subtitle: "Las plataformas empresariales sobre las que construimos.",
      cards: [
        "SAP", "ABAP", "SAP BTP", "SAP Build Process Automation",
        "OData", "ABAP RAP", "Flowable", "n8n",
        "React", "TypeScript", "Python", "FastAPI",
        "Anthropic Claude", "OpenAI", "Google Gemini",
        "PostgreSQL", "Docker", "REST APIs",
        "Pontis (nuestro toolkit de IA para SAP)"
      ]
    },
    lab: {
      headline: "Del concepto al prototipo funcional",
      cards: [
        { title: "Reemplazo de Flujo Heredado", type: "Demo de Proceso", img: "/Episteck_Service_Visuals/visual-workflow-modernization.svg" },
        { title: "Aprobación Conectada a SAP", type: "Demo de Integración", img: "/Episteck_Service_Visuals/visual-seamless-integration.svg" },
        { title: "Entrega de Flujos con IA", type: "Demo de Ingeniería", img: "/Episteck_Service_Visuals/visual-process-orchestration.svg" }
      ]
    },
    assessment: {
      headline: "Comience con una Evaluación de Modernización",
      items: [
        "Revisión del estado actual", "Oportunidades de modernización",
        "Arquitectura objetivo", "Hoja de ruta de automatización", "Evaluación de riesgos", "Plan de implementación estimado"
      ],
      btn: "Solicitar Evaluación"
    },
    footerCTA: {
      headline: "¿Planea reemplazar un flujo de trabajo heredado en SAP?",
      btn: "Hable con Episteck",
      copyright: "© 2026 Episteck. Consultora especializada en modernización."
    }
  }
};

// --- PILLAR IMAGES ---
// Curated Unsplash photos, one per pillar (Process / Software / Automation / AI).
// Indexed in the same order as t.hero.pillars in the translations dict.
// Sized to ~600px wide which is plenty for the card thumbnails AND the modal
// header (~750px wide); avoids loading the full-resolution photos.
const PILLAR_IMAGES = [
  // Process — whiteboard with flowchart sticky notes
  'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&q=80&auto=format&fit=crop',
  // Software — developer screen with code
  'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=600&q=80&auto=format&fit=crop',
  // Automation — industrial robot arm
  'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=600&q=80&auto=format&fit=crop',
  // AI — abstract neural / data visualization
  'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&q=80&auto=format&fit=crop',
];

// --- MAIN COMPONENT ---
type Lang = 'en' | 'es';
type Theme = 'light' | 'dark';

export default function EpisteckLanding() {
  const [lang, setLang] = useState<Lang>('es');
  const [theme, setTheme] = useState<Theme>('light');

  // Which branch the visitor is engaging with. The Growth CTA sets this to
  // 'growth'; the Enterprise/generic CTAs reset it to 'enterprise'. It rides
  // along on the mapper submission so the resulting Lead is tagged by branch.
  const [branch, setBranch] = useState<Branch>('enterprise');
  const [contactOpen, setContactOpen] = useState(false);

  // Ref the hero TimelineContent watches to drive the staggered reveal.
  const heroRef = useRef<HTMLElement | null>(null);

  const t = translations[lang];

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  const toggleTheme = () => setTheme(theme === 'light' ? 'dark' : 'light');
  const toggleLang = () => setLang(lang === 'en' ? 'es' : 'en');

  return (
    <div className={`min-h-screen font-sans transition-colors duration-300 ${theme === 'dark' ? 'bg-slate-950 text-slate-100' : 'bg-slate-50 text-slate-900'}`}>
      
      {/* Navigation */}
      <nav className={`fixed w-full z-50 border-b backdrop-blur-md ${theme === 'dark' ? 'border-slate-800 bg-slate-950/80' : 'border-slate-200 bg-white/80'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <img
                src={
                  theme === 'dark'
                    ? '/Episteck_Logo_Pack/episteck-icon-dark.svg'
                    : '/Episteck_Logo_Pack/episteck-icon-light.svg'
                }
                alt="Episteck"
                className="w-8 h-8"
                width={32}
                height={32}
              />
              <span className="font-bold text-xl tracking-tight">Episteck</span>
            </div>
            
            <div className="hidden lg:flex space-x-6 items-center text-sm">
              <a href="#services" className="hover:text-blue-500 transition-colors">{t.nav.services}</a>
              <a href="#enterprise" className="hover:text-blue-500 transition-colors">{t.nav.enterprise}</a>
              <a href="#growth" className="hover:text-blue-500 transition-colors">{t.nav.growth}</a>
              <a href="#ai" className="hover:text-blue-500 transition-colors">{t.nav.ai}</a>
              <a href="#mapper" className="hover:text-blue-500 transition-colors">{t.nav.mapper}</a>
              <a href="#about" className="hover:text-blue-500 transition-colors">{t.nav.about}</a>
              <a href="#contact" className="hover:text-blue-500 transition-colors">{t.nav.contact}</a>
            </div>

            <div className="flex items-center space-x-4">
              <button onClick={toggleLang} className="flex items-center space-x-1 hover:text-blue-500">
                <Globe size={18} />
                <span className="uppercase text-sm font-medium">{lang}</span>
              </button>
              <button onClick={toggleTheme} className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors">
                {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="pt-16">
        {/* HERO SECTION — text on the left + interactive image accordion on the right.
            White canvas (per design); the rest of the page keeps its dark/light
            theming via Tailwind's `dark:` variants further down. */}
        {/* HERO SECTION — text on the left + interactive image accordion on the right.
            AuroraBackground provides an animated gradient backdrop; we drop the
            section's own bg color so AuroraBackground's bg-zinc-50 / dark:bg-zinc-900
            shows through. */}
        <section
          ref={heroRef}
          className="relative border-b border-slate-200 dark:border-slate-800"
        >
          <AuroraBackground className="min-h-[600px]">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 lg:py-24 relative z-10">
              <div className="flex flex-col lg:flex-row items-center justify-between gap-10 lg:gap-12">

              {/* LEFT: Badge + Headline + Subhead + CTAs */}
              <div className="w-full lg:w-1/2 text-center lg:text-left">
                <TimelineContent
                  as="div"
                  animationNum={0}
                  timelineRef={heroRef}
                  className="inline-flex items-center rounded-full px-3 py-1 text-sm font-medium text-blue-700 bg-blue-100 dark:text-blue-300 dark:bg-blue-500/10 ring-1 ring-blue-200 dark:ring-blue-400/30 mb-6"
                >
                  <Activity size={16} className="mr-2" />
                  {t.hero.badge}
                </TimelineContent>

                <TimelineContent
                  as="h1"
                  animationNum={1}
                  timelineRef={heroRef}
                  className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white leading-tight tracking-tight mb-6"
                >
                  {t.hero.headline.split(t.hero.headlineAccent)[0]}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-teal-500">{t.hero.headlineAccent}</span>
                  {t.hero.headline.split(t.hero.headlineAccent)[1] ?? ''}
                </TimelineContent>

                <TimelineContent
                  as="p"
                  animationNum={2}
                  timelineRef={heroRef}
                  className="text-lg text-slate-600 dark:text-slate-300 max-w-xl mx-auto lg:mx-0 mb-8 leading-relaxed"
                >
                  {t.hero.subhead}
                </TimelineContent>

                <TimelineContent
                  as="div"
                  animationNum={3}
                  timelineRef={heroRef}
                  className="flex flex-col sm:flex-row justify-center lg:justify-start items-center gap-4"
                >
                  <button
                    type="button"
                    onClick={() => setContactOpen(true)}
                    className="w-full sm:w-auto px-7 py-3.5 bg-slate-900 dark:bg-blue-600 hover:bg-slate-800 dark:hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors shadow-lg flex items-center justify-center"
                  >
                    {t.hero.ctaPrimary}
                    <ArrowRight size={18} className="ml-2" />
                  </button>
                  <a
                    href="#enterprise"
                    className="w-full sm:w-auto px-7 py-3.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-900 dark:text-white rounded-lg font-semibold transition-colors flex items-center justify-center"
                  >
                    {t.hero.ctaSecondary}
                  </a>
                </TimelineContent>
              </div>

              {/* RIGHT: Interactive image accordion (Process / Software / Automation / AI) */}
              <TimelineContent
                as="div"
                animationNum={4}
                timelineRef={heroRef}
                className="w-full lg:w-1/2"
              >
                <InteractiveImageAccordion
                  items={t.hero.pillars.map((p, idx) => ({
                    id: p.id,
                    title: p.title,
                    subtitle: p.short,
                    imageUrl: PILLAR_IMAGES[idx],
                  }))}
                  // Default to the last pillar expanded (matches the source design)
                  initialActiveIndex={t.hero.pillars.length - 1}
                />
              </TimelineContent>

            </div>
          </div>
          </AuroraBackground>
        </section>

        {/* PROBLEM SECTION */}
        <section id="services" className="py-24 bg-white dark:bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-center text-center mb-16">
              <EyebrowLabel accent="neutral" centered className="mb-5">
                {lang === 'es' ? 'El problema' : 'The problem'}
              </EyebrowLabel>
              <h2 className="text-3xl md:text-4xl font-serif">{t.problem.headline}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {t.problem.cards.map((card, idx) => {
                // Index → icon filename. Order matches the translations dict in
                // both EN and ES (verified). Custom SVGs use stroke=currentColor
                // and we render them as a CSS mask so the icon picks up the
                // surrounding text-red-600 color (works in light + dark mode).
                const painIcons = [
                  '/Episteck_Pain_Icons/icon-fragmented-processes.svg',
                  '/Episteck_Pain_Icons/icon-legacy-ux.svg',
                  '/Episteck_Pain_Icons/icon-low-traceability.svg',
                  '/Episteck_Pain_Icons/icon-slow-cycles.svg',
                  '/Episteck_Pain_Icons/icon-operational-dependency.svg',
                ];
                const iconUrl = painIcons[idx] ?? painIcons[0];
                return (
                  <div key={idx} className="p-6 rounded-2xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 hover:border-blue-500/50 transition-colors">
                    <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg flex items-center justify-center mb-4">
                      {/* Render the SVG as a CSS mask so it inherits text color. */}
                      <span
                        className="block w-6 h-6 bg-current"
                        style={{
                          WebkitMaskImage: `url('${iconUrl}')`,
                          maskImage: `url('${iconUrl}')`,
                          WebkitMaskRepeat: 'no-repeat',
                          maskRepeat: 'no-repeat',
                          WebkitMaskPosition: 'center',
                          maskPosition: 'center',
                          WebkitMaskSize: 'contain',
                          maskSize: 'contain',
                        }}
                        aria-hidden="true"
                      />
                    </div>
                    <h3 className="text-xl font-bold mb-2">{card.title}</h3>
                    <p className="text-slate-600 dark:text-slate-400">{card.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* SOLUTION BRIDGE */}
        <SolutionBridge t={t} />

        {/* ENTERPRISE SECTION */}
        <section id="enterprise" className="py-24 bg-white dark:bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center mb-16 flex flex-col items-center">
              <EyebrowLabel accent="enterprise" centered className="mb-5">
                {t.enterprise.eyebrow}
              </EyebrowLabel>
              <h2 className="text-3xl md:text-4xl font-serif mb-4">{t.enterprise.headline}</h2>
              <p className="text-lg text-slate-600 dark:text-slate-300">{t.enterprise.subtitle}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {t.enterprise.cards.map((card: { pain: string; title: string; desc: string; bullets: string[] }, idx: number) => {
                const icons = [<Compass size={24} />, <Sparkles size={24} />, <Server size={24} />];
                const isFeatured = idx === 1;
                return (
                  <div
                    key={idx}
                    className={`flex flex-col p-8 rounded-2xl bg-slate-50 dark:bg-slate-950 border transition-colors ${
                      isFeatured
                        ? 'border border-slate-200 dark:border-slate-800 border-l-4 border-l-enterprise'
                        : 'border-slate-200 dark:border-slate-800 hover:border-enterprise/60'
                    }`}
                  >
                    {/* Pain line */}
                    <p className="text-sm italic text-slate-400 dark:text-slate-500 mb-4 leading-relaxed">
                      "{card.pain}"
                    </p>
                    {/* Amber rule */}
                    <div className="w-8 h-0.5 bg-enterprise mb-4" />
                    {/* Icon + title */}
                    <div className="w-12 h-12 rounded-lg bg-enterprise/10 text-enterprise-dark dark:text-enterprise flex items-center justify-center mb-4">
                      {icons[idx] ?? icons[0]}
                    </div>
                    <h3 className="text-xl font-bold mb-2">{card.title}</h3>
                    <p className="text-slate-600 dark:text-slate-400 mb-5">{card.desc}</p>
                    <ul className="mt-auto space-y-2">
                      {card.bullets.map((b: string, i: number) => (
                        <li key={i} className="flex items-start text-sm text-slate-600 dark:text-slate-400">
                          <Check size={16} className="mr-2 mt-0.5 shrink-0 text-enterprise-dark dark:text-enterprise" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                    {/* Pontis badge — only on card index 1 (Automation & AI) */}
                    {isFeatured && (
                      <div className="mt-5 flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/40">
                        <div className="w-1.5 h-1.5 rounded-full bg-enterprise mt-1.5 shrink-0" />
                        <div>
                          <span className="text-xs font-bold tracking-widest text-enterprise-dark dark:text-enterprise uppercase">Pontis</span>
                          <span className="text-xs text-slate-500 dark:text-slate-500 ml-2">SAP AI Toolkit — audited agent access to ABAP</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Pontis IP band */}
            <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 p-5 rounded-xl bg-slate-900 dark:bg-slate-950 border border-slate-800">
              <span className="text-xs font-bold tracking-widest text-enterprise uppercase shrink-0">
                {t.enterprise.pontis.label}
              </span>
              <div className="hidden sm:block w-px h-6 bg-slate-700 shrink-0" />
              <p className="text-sm text-slate-400 leading-relaxed">
                <strong className="text-enterprise">{t.enterprise.pontis.name}</strong>
                {' — '}
                {t.enterprise.pontis.desc}
              </p>
            </div>
            {/* Enterprise CTA */}
            <div className="mt-10 text-center">
              <a
                href="#mapper"
                onClick={() => setBranch('enterprise')}
                className="inline-flex items-center px-8 py-4 rounded-lg font-semibold text-white bg-slate-900 dark:bg-enterprise hover:bg-slate-800 dark:hover:bg-enterprise-dark transition-colors shadow-lg"
              >
                {t.enterprise.cta}
                <ArrowRight size={18} className="ml-2" />
              </a>
              <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">{t.enterprise.ctaNote}</p>
            </div>
          </div>
        </section>

        {/* EPISTECK GROWTH SECTION — public second branch. SaaS-style plan grid. */}
        <section id="growth" className="py-24 bg-slate-50 dark:bg-slate-950 border-y border-slate-200 dark:border-slate-800">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mb-12">
              <EyebrowLabel accent="growth" className="mb-5">
                {t.growth.eyebrow}
              </EyebrowLabel>
              <h2 className="text-3xl md:text-4xl font-serif mb-4">{t.growth.headline}</h2>
              <QuoteBlock accent="growth" className="mb-4">{t.growth.quote}</QuoteBlock>
              <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed">{t.growth.intro}</p>
            </div>
            <GrowthPlansGrid
              label={t.growth.plansLabel}
              plans={t.growth.plans}
              cta={t.growth.plansCta}
              ctaNote={t.growth.plansCtaNote}
              footnote={t.growth.plansFootnote}
              featuredBadge={t.growth.featuredBadge}
              onCtaClick={() => setBranch('growth')}
            />
          </div>
        </section>

        {/* PRACTICAL AI SECTION */}
        <section id="ai" className="py-24 bg-slate-50 dark:bg-slate-950 border-y border-slate-200 dark:border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center mb-16 flex flex-col items-center">
              <EyebrowLabel accent="enterprise" centered className="mb-5">
                {t.aiSection.eyebrow}
              </EyebrowLabel>
              <h2 className="text-3xl md:text-4xl font-serif mb-4">{t.aiSection.headline}</h2>
              <p className="text-lg text-slate-600 dark:text-slate-300">{t.aiSection.subtitle}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {t.aiSection.capabilities.map((cap, idx) => {
                // One distinct glyph per capability (order matches the translations
                // dict). Amber brand-token container matches the Enterprise cards.
                const capIcons = [
                  <MessageSquare size={20} />, // natural-language requests
                  <FileText size={20} />,      // extract from documents
                  <FileSearch size={20} />,    // summarize & explain
                  <Compass size={20} />,       // suggest next action
                  <Zap size={20} />,           // reduce repetitive work
                  <ShieldCheck size={20} />,   // approvals, human in control
                ];
                return (
                  <div key={idx} className="flex p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-enterprise/60 transition-colors">
                    <div className="flex-shrink-0 mr-4">
                      <div className="w-11 h-11 rounded-lg bg-enterprise/10 text-enterprise-dark dark:text-enterprise flex items-center justify-center">
                        {capIcons[idx] ?? capIcons[0]}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-bold mb-1">{cap.title}</h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400">{cap.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Pontis toolkit callout — moved to Enterprise section (2026-08-04 redesign) */}
            {false && (
            <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-start gap-6 p-8 rounded-2xl bg-slate-900 dark:bg-slate-950 border border-slate-800 text-white">
              <img
                src="/Episteck_Service_Visuals/visual-ai-automation.svg"
                alt="Diagram of AI-assisted automation connected to SAP"
                className="w-full md:w-56 h-auto rounded-xl flex-shrink-0"
                width={224}
                height={168}
                loading="lazy"
              />
              <div>
                <span className="text-xs font-semibold uppercase tracking-wide text-teal-400">
                  {t.aiSection.toolkitLabel}
                </span>
                <h3 className="text-xl font-bold mt-1 mb-2 flex items-center">
                  <ShieldCheck className="text-teal-400 mr-2" size={22} />
                  {t.aiSection.toolkitTitle}
                </h3>
                <p className="text-slate-300 leading-relaxed">{t.aiSection.toolkitDesc}</p>
              </div>
            </div>
            )}
          </div>
        </section>

        {/* INTERACTIVE WORKFLOW MAPPER */}
        <WorkflowMapperSection t={t} theme={theme} lang={lang} branch={branch} />

        {/* OUR APPROACH — removed per 2026-08-04 redesign (generic, no differentiation) */}
        {false && (
        <section id="approach" className="py-24 bg-slate-50 dark:bg-slate-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-center">
            <RoadmapCard
              title={t.approach.headline}
              description={lang === 'es'
                ? 'Cinco fases. Riesgo controlado. Resultados medibles en cada paso.'
                : 'Five phases. Controlled risk. Measurable results at every step.'}
              items={t.approach.phases.map((phase, idx) => ({
                quarter: lang === 'es' ? `Fase ${idx + 1}` : `Phase ${idx + 1}`,
                title: phase.title,
                description: phase.desc,
                // First phase is the active one (where engagements always start);
                // remaining phases are upcoming. None are "done" in the
                // methodology display — this is a forward-looking process map.
                status: idx === 0 ? 'in-progress' : 'upcoming',
              }))}
              className="w-full max-w-6xl shadow-xl hover:shadow-2xl transition-shadow duration-300"
            />
          </div>
        </section>
        )}

        {/* WHY EPISTECK */}
        <section className="py-24 bg-slate-50 dark:bg-slate-950 border-y border-slate-200 dark:border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-center text-center mb-16">
              <EyebrowLabel accent="neutral" centered className="mb-5">
                {lang === 'es' ? 'Por qué Episteck' : 'Why Episteck'}
              </EyebrowLabel>
              <h2 className="text-3xl md:text-4xl font-serif">{t.whyEpisteck.headline}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {t.whyEpisteck.cards.map((card, idx) => {
                // Distinct glyph per differentiator (order matches translations):
                // workflow-first, senior SAP, accelerated delivery, start-small.
                const whyIcons = [
                  <Network size={24} />,     // workflow-first
                  <Server size={24} />,      // senior SAP understanding
                  <Zap size={24} />,         // accelerated, senior-led delivery
                  <Compass size={24} />,     // start small, no lock-in
                ];
                return (
                  <div key={idx} className="flex p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-growth/60 transition-colors">
                    <div className="flex-shrink-0 mr-4">
                      <div className="w-12 h-12 rounded-lg bg-growth/10 text-growth-dark dark:text-growth flex items-center justify-center">
                        {whyIcons[idx] ?? whyIcons[0]}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2">{card.title}</h3>
                      <p className="text-slate-600 dark:text-slate-400">{card.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* TRANSFORMATION EXAMPLES (illustrative) */}
        <section className="py-24 bg-white dark:bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center mb-16 flex flex-col items-center">
              <EyebrowLabel accent="neutral" centered className="mb-5">
                {lang === 'es' ? 'En la práctica' : 'In practice'}
              </EyebrowLabel>
              <h2 className="text-3xl md:text-4xl font-serif mb-4">{t.examples.headline}</h2>
              <p className="text-lg text-slate-600 dark:text-slate-300">{t.examples.subtitle}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {t.examples.cases.map((c, idx) => (
                <div key={idx} className="flex flex-col p-6 rounded-2xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold">{c.name}</h3>
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded px-2 py-1">
                      {t.examples.disclaimer}
                    </span>
                  </div>
                  <div className="space-y-4 text-sm">
                    <div>
                      <div className="flex items-center font-semibold text-slate-500 dark:text-slate-400 mb-1">
                        <AlertCircle size={14} className="mr-1.5 text-red-500" /> {t.examples.labels.problem}
                      </div>
                      <p className="text-slate-600 dark:text-slate-400">{c.problem}</p>
                    </div>
                    <div>
                      <div className="flex items-center font-semibold text-slate-500 dark:text-slate-400 mb-1">
                        <RefreshCw size={14} className="mr-1.5 text-blue-500" /> {t.examples.labels.solution}
                      </div>
                      <p className="text-slate-600 dark:text-slate-400">{c.solution}</p>
                    </div>
                    <div>
                      <div className="flex items-center font-semibold text-slate-500 dark:text-slate-400 mb-1">
                        <CheckCircle2 size={14} className="mr-1.5 text-teal-500" /> {t.examples.labels.result}
                      </div>
                      <p className="text-slate-600 dark:text-slate-400">{c.result}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ABOUT / WHAT IS EPISTECK */}
        <section id="about" className="py-24 bg-white dark:bg-slate-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-center text-center mb-6">
              <EyebrowLabel accent="neutral" centered className="mb-5">
                {lang === 'es' ? 'Sobre nosotros' : 'About us'}
              </EyebrowLabel>
              <h2 className="text-3xl md:text-4xl font-serif">{t.about.headline}</h2>
            </div>
            <QuoteBlock accent="enterprise" className="mb-10 text-center">{t.about.lead}</QuoteBlock>
            <div className="space-y-5 text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              {t.about.paragraphs.map((para: string, idx: number) => (
                <p key={idx}>{para}</p>
              ))}
            </div>
            <div className="mt-10 p-6 rounded-2xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400 mb-2">
                {t.about.forWhoLabel}
              </h3>
              <p className="text-slate-600 dark:text-slate-300">{t.about.forWho}</p>
            </div>
          </div>
        </section>

        {/* PLATFORMS */}
        <section className="py-24 bg-white dark:bg-slate-900 text-center">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center">
            <EyebrowLabel accent="neutral" centered className="mb-5">
              {lang === 'es' ? 'Tecnologías' : 'Technologies'}
            </EyebrowLabel>
            <h2 className="text-3xl md:text-4xl font-serif mb-4">{t.tech.headline}</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-12">{t.tech.subtitle}</p>
            <div className="flex flex-wrap justify-center gap-4">
              {t.tech.cards.map((tech, idx) => (
                <span key={idx} className="px-5 py-3 rounded-full bg-slate-100 dark:bg-slate-800 text-sm font-medium text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-700">
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* WORKFLOW LAB — hidden per client request (2026-07-29). Kept in the
            tree behind a `false` guard so it's trivially restorable; the `lab`
            translations remain in the dict. Flip to re-enable. */}
        {false && (
        <section className="py-24 bg-slate-900 dark:bg-black text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-center text-center mb-12">
              <EyebrowLabel accent="growth" centered className="mb-5">
                {lang === 'es' ? 'Laboratorio' : 'Lab'}
              </EyebrowLabel>
              <h2 className="text-3xl md:text-4xl font-serif">{t.lab.headline}</h2>
            </div>
            <CardCarousel
              autoplayDelay={3500}
              showPagination
              showNavigation={false}
              images={t.lab.cards.map((demo) => ({
                src: demo.img,
                alt: `${demo.type} — ${demo.title}`,
              }))}
            />
          </div>
        </section>
        )}

        {/* ASSESSMENT CTA */}
        <section id="contact" className="py-24 bg-blue-600 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
            <h2 className="text-3xl md:text-5xl font-bold mb-8">{t.assessment.headline}</h2>
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {t.assessment.items.map((item, idx) => (
                <div key={idx} className="flex items-center bg-blue-700/50 rounded-full px-4 py-2 text-sm font-medium">
                  <CheckCircle2 size={16} className="mr-2 text-teal-300" />
                  {item}
                </div>
              ))}
            </div>
            <a
              href="#mapper"
              onClick={() => setBranch('enterprise')}
              className="inline-flex px-8 py-4 bg-white text-blue-600 hover:bg-slate-100 rounded-lg font-bold text-lg transition-all shadow-xl"
            >
              {t.assessment.btn}
            </a>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="bg-slate-50 dark:bg-slate-950 py-12 border-t border-slate-200 dark:border-slate-800 text-center">
        <div className="max-w-7xl mx-auto px-4">
          <h3 className="text-2xl font-bold mb-6">{t.footerCTA.headline}</h3>
          <a
            href="#mapper"
            onClick={() => setBranch('enterprise')}
            className="inline-flex px-6 py-3 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg font-semibold hover:bg-slate-800 dark:hover:bg-slate-100 transition-colors mb-12"
          >
            {t.footerCTA.btn}
          </a>
          <div className="flex items-center justify-center space-x-2 text-slate-500 text-sm">
            <img
              src={
                theme === 'dark'
                  ? '/Episteck_Logo_Pack/episteck-icon-dark.svg'
                  : '/Episteck_Logo_Pack/episteck-icon-light.svg'
              }
              alt=""
              aria-hidden="true"
              className="w-6 h-6"
              width={24}
              height={24}
            />
            <span>{t.footerCTA.copyright}</span>
          </div>
        </div>
      </footer>
      <ContactDialog
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        t={t}
        lang={lang}
        branch={branch}
      />
    </div>
  );
}

// --- WORKFLOW MAPPER COMPONENT ---
type FormData = {
  processName: string;
  description: string;
  trigger: string;
  roles: string;
  approvals: string;
  systems: string;
  sapRole: string;
  painPoints: string;
  outcome: string;
};

type LeadData = {
  name: string;
  company: string;
  email: string;
  phone: string;
  consent: boolean;
};

interface MapperProps {
  // `t` is a slice of the translations object; we keep it loosely typed because
  // the original translations dict is wide and not the focus of this refactor.
  t: any;
  theme: Theme;
  lang: Lang;
  branch: Branch;
}

function WorkflowMapperSection({ t, theme: _theme, lang, branch }: MapperProps) {
  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5>(1); // 1: Form, 2: Loading, 3: Preview, 4: Lead, 5: Success
  const [loadingTextIndex, setLoadingTextIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Holds the API response after step 1 succeeds. The preview (step 3) renders
  // FROM this object — so what the user sees is exactly what's in the Lead.
  const [assessment, setAssessment] = useState<AssessmentResponse | null>(null);

  const [formData, setFormData] = useState<FormData>({
    processName: '', description: '', trigger: '', roles: '',
    approvals: '', systems: '', sapRole: '', painPoints: '', outcome: '',
  });

  const [leadData, setLeadData] = useState<LeadData>({
    name: '', company: '', email: '', phone: '', consent: false,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleLeadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
    setLeadData({ ...leadData, [e.target.name]: value });
  };

  // Map the FE form snake-case-ish field names → backend Pydantic field names.
  const buildRequestBody = (includeContact: boolean) => ({
    process_name: formData.processName,
    description: formData.description,
    trigger: formData.trigger,
    roles: formData.roles,
    approvals: formData.approvals,
    systems_involved: formData.systems,
    sap_touchpoint: formData.sapRole,
    pain_points: formData.painPoints,
    desired_outcome: formData.outcome,
    language: lang,
    branch,
    contact: includeContact
      ? {
          name: leadData.name,
          company: leadData.company,
          email: leadData.email,
          phone: leadData.phone || null,
          consent: leadData.consent,
        }
      : null,
    assessment_id: assessment?.assessment_id ?? null,
  });

  // Phase 1: generate the workflow map. Animates the loading texts WHILE the
  // request is in flight, then transitions to the preview when both finish.
  const generateMap = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.processName.trim()) return;
    setError(null);
    setSubmitting(true);
    setStep(2);
    setLoadingTextIndex(0);

    // Cycle the loading copy as a UX rhythm. The API call runs in parallel.
    const loadingFrames: string[] = t.mapper.loading;
    let frame = 0;
    const interval = setInterval(() => {
      frame = (frame + 1) % loadingFrames.length;
      setLoadingTextIndex(frame);
    }, 1100);

    try {
      const result = await postWorkflowAssessment(buildRequestBody(false));
      clearInterval(interval);
      setAssessment(result);
      setStep(3);
    } catch (err) {
      clearInterval(interval);
      const msg = err instanceof Error ? err.message : 'Something went wrong.';
      setError(msg);
      setStep(1);
    } finally {
      setSubmitting(false);
    }
  };

  // Phase 2: send the contact + create the Lead in ERPNext.
  const submitLead = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await postWorkflowAssessment(buildRequestBody(true));
      setAssessment(result);
      setStep(5);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not send your workflow.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => {
    setStep(1);
    setAssessment(null);
    setError(null);
    setFormData({
      processName: '', description: '', trigger: '', roles: '',
      approvals: '', systems: '', sapRole: '', painPoints: '', outcome: '',
    });
    setLeadData({ name: '', company: '', email: '', phone: '', consent: false });
  };

  const renderInput = (
    name: keyof FormData,
    label: string,
    placeholder: string,
    isTextarea = false,
  ) => (
    <div className="flex flex-col">
      <label className="text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">{label}</label>
      {isTextarea ? (
        <textarea
          name={name} value={formData[name]} onChange={handleInputChange} placeholder={placeholder} rows={2}
          className="p-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500 outline-none transition-all resize-none"
        />
      ) : (
        <input
          type="text" name={name} value={formData[name]} onChange={handleInputChange} placeholder={placeholder}
          className="p-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
          required={name === 'processName'}
        />
      )}
    </div>
  );

  // ─── Diagram nodes from API response ────────────────────────────────────────
  // The backend returns a list of typed nodes. We pick an icon + color per
  // type so the visual stays consistent with the original 4-node design.
  const NODE_VISUALS: Record<string, { icon: React.ReactNode; color: string; isSAP?: boolean }> = {
    start:          { icon: <PlayCircle />, color: 'border-green-500' },
    legacy_systems: { icon: <Layers />,     color: 'border-slate-500' },
    approval:       { icon: <Lock />,       color: 'border-amber-500' },
    sap:            { icon: <Database />,   color: 'border-blue-500', isSAP: true },
    modern_ui:      { icon: <MonitorPlay />, color: 'border-teal-500' },
    automation:     { icon: <Zap />,        color: 'border-purple-500' },
    end:            { icon: <CheckCircle2 />, color: 'border-green-600' },
  };

  return (
    <section id="mapper" className="py-24 bg-gradient-to-b from-slate-100 to-white dark:from-slate-900 dark:to-slate-950 relative">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-serif mb-4">{t.mapper.headline}</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">{t.mapper.subtitle}</p>
        </div>

        <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl overflow-hidden relative min-h-[500px]">

          {/* STEP 1: FORM */}
          {step === 1 && (
            <div className="p-8 md:p-12 animate-in fade-in zoom-in-95 duration-300">
              <div className="flex items-center mb-8 border-b border-slate-200 dark:border-slate-800 pb-4">
                <Settings className="text-blue-500 mr-3" />
                <h3 className="text-2xl font-bold">{t.mapper.step1.title}</h3>
              </div>
              {error && (
                <div className="mb-6 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm flex items-start">
                  <AlertCircle size={18} className="mr-2 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              <form onSubmit={generateMap} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {renderInput('processName', t.mapper.step1.processName, t.mapper.step1.processNamePh)}
                  {renderInput('trigger', t.mapper.step1.trigger, t.mapper.step1.triggerPh)}
                  {renderInput('roles', t.mapper.step1.roles, t.mapper.step1.rolesPh)}
                  {renderInput('approvals', t.mapper.step1.approvals, t.mapper.step1.approvalsPh)}
                  {renderInput('systems', t.mapper.step1.systems, t.mapper.step1.systemsPh)}
                  {renderInput('sapRole', t.mapper.step1.sapRole, t.mapper.step1.sapRolePh)}
                </div>
                {renderInput('description', t.mapper.step1.description, t.mapper.step1.descriptionPh, true)}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {renderInput('painPoints', t.mapper.step1.painPoints, t.mapper.step1.painPointsPh, true)}
                  {renderInput('outcome', t.mapper.step1.outcome, t.mapper.step1.outcomePh, true)}
                </div>
                <div className="pt-6 flex justify-end">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-8 py-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white rounded-lg font-bold flex items-center transition-all shadow-lg hover:shadow-blue-500/30"
                  >
                    {t.mapper.step1.generateBtn}
                    <Activity className="ml-2" size={20} />
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* STEP 2: LOADING */}
          {step === 2 && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/90 dark:bg-slate-950/90 backdrop-blur-sm animate-in fade-in duration-300">
              <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-6"></div>
              <p className="text-xl font-medium text-slate-800 dark:text-slate-200 h-8">
                {t.mapper.loading[loadingTextIndex] || t.mapper.loading[t.mapper.loading.length - 1]}
              </p>
            </div>
          )}

          {/* STEP 3: PREVIEW MAP */}
          {step === 3 && assessment && (
            <div className="p-8 md:p-12 animate-in slide-in-from-right duration-500">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 border-b border-slate-200 dark:border-slate-800 pb-4">
                <div>
                  <h3 className="text-2xl font-bold flex items-center">
                    <Activity className="text-blue-500 mr-3" />
                    {t.mapper.preview.title}: {formData.processName || 'Legacy Process'}
                  </h3>
                  <p className="text-sm text-amber-600 dark:text-amber-400 mt-1 flex items-center">
                    <AlertCircle size={14} className="mr-1" /> {t.mapper.preview.disclaimer}
                  </p>
                  {assessment.summary && (
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-3 max-w-2xl">
                      {assessment.summary}
                    </p>
                  )}
                </div>
                <button onClick={() => setStep(4)} className="mt-4 md:mt-0 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold flex items-center transition-all">
                  {t.mapper.preview.nextStepBtn} <ChevronRight size={18} className="ml-1" />
                </button>
              </div>

              {/* BPMN diagram from the API. Backend transcodes the
                  generated_map into BPMN 2.0 XML and we render it with
                  bpmn-js (same library Parmalat's viewer uses).
                  Preview-only; never deployed to the live Flowable engine. */}
              <div className="w-full pb-8">
                <BpmnViewer
                  xml={assessment.generated_bpmn_xml}
                  className="w-full h-[420px] border border-slate-200 dark:border-slate-800 rounded-lg"
                  downloadName={(formData.processName || 'workflow').toLowerCase().replace(/\s+/g, '-')}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-4 bg-slate-50 dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800">
                <div>
                  <h4 className="font-bold text-lg mb-4 flex items-center">
                    <Zap className="text-teal-500 mr-2" size={20} /> {t.mapper.preview.opportunitiesTitle}
                  </h4>
                  <ul className="space-y-3">
                    {assessment.opportunities.map((opp, i) => (
                      <li key={i} className="flex items-start text-sm">
                        <CheckCircle2
                          size={16}
                          className={`mr-2 mt-0.5 shrink-0 ${
                            opp.impact === 'high'
                              ? 'text-teal-500'
                              : opp.impact === 'medium'
                                ? 'text-blue-500'
                                : 'text-slate-400'
                          }`}
                        />
                        <span>
                          <strong className="text-slate-900 dark:text-slate-100">{opp.title}.</strong>{' '}
                          <span className="text-slate-600 dark:text-slate-400">{opp.detail}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-4 flex items-center">
                    <Cpu className="text-blue-500 mr-2" size={20} /> Architecture Recommendation
                  </h4>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                    {lang === 'es' ? 'Enfoque recomendado: ' : 'Recommended approach: '}
                    <strong className="text-slate-900 dark:text-slate-100">
                      {assessment.recommendation.approach}
                    </strong>
                    {'. '}
                    {lang === 'es' ? 'Complejidad estimada: ' : 'Estimated complexity: '}
                    <strong className="text-slate-900 dark:text-slate-100">
                      {assessment.recommendation.estimated_complexity}
                    </strong>
                    .
                  </p>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {assessment.recommendation.stack_tags.map((tag, i) => (
                      <span
                        key={i}
                        className="px-2 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-xs rounded font-mono"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  {assessment.recommendation.next_step && (
                    <p className="text-xs text-slate-500 dark:text-slate-500 mt-4 italic">
                      → {assessment.recommendation.next_step}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* STEP 4: LEAD FORM */}
          {step === 4 && (
            <div className="p-8 md:p-12 max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-10 duration-500">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold mb-2">{t.mapper.lead.title}</h3>
                <p className="text-slate-600 dark:text-slate-400">{t.mapper.lead.subtitle}</p>
              </div>
              {error && (
                <div className="mb-6 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm flex items-start">
                  <AlertCircle size={18} className="mr-2 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              <form onSubmit={submitLead} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium mb-1">{t.mapper.lead.name}</label>
                    <input type="text" name="name" required value={leadData.name} onChange={handleLeadChange} className="p-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500 outline-none" />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-sm font-medium mb-1">{t.mapper.lead.company}</label>
                    <input type="text" name="company" required value={leadData.company} onChange={handleLeadChange} className="p-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500 outline-none" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium mb-1">{t.mapper.lead.email}</label>
                    <input type="email" name="email" required value={leadData.email} onChange={handleLeadChange} className="p-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500 outline-none" />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-sm font-medium mb-1">{t.mapper.lead.phone}</label>
                    <input type="tel" name="phone" value={leadData.phone} onChange={handleLeadChange} className="p-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500 outline-none" />
                  </div>
                </div>
                <div className="flex items-start mt-4">
                  <input type="checkbox" name="consent" required checked={leadData.consent} onChange={handleLeadChange} className="mt-1 mr-3 rounded text-blue-600 focus:ring-blue-500" />
                  <label className="text-sm text-slate-600 dark:text-slate-400">{t.mapper.lead.consent}</label>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-4 mt-6 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white rounded-lg font-bold text-lg transition-all"
                >
                  {submitting
                    ? lang === 'es' ? 'Enviando…' : 'Sending…'
                    : t.mapper.lead.submitBtn}
                </button>
              </form>
            </div>
          )}

          {/* STEP 5: SUCCESS */}
          {step === 5 && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center animate-in zoom-in duration-500">
              <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 text-green-500 rounded-full flex items-center justify-center mb-6">
                <CheckCircle2 size={40} />
              </div>
              <h3 className="text-3xl font-bold mb-4">{t.mapper.success.title}</h3>
              <p className="text-lg text-slate-600 dark:text-slate-400 max-w-md">{t.mapper.success.message}</p>
              {assessment?.lead_id && (
                <p className="text-xs text-slate-400 dark:text-slate-600 mt-4 font-mono">
                  Ref: {assessment.lead_id}
                </p>
              )}
              <button onClick={restart} className="mt-8 text-blue-600 dark:text-blue-400 font-medium hover:underline">
                {lang === 'es' ? 'Iniciar un nuevo mapa' : 'Start a new map'}
              </button>
            </div>
          )}

        </div>
      </div>
    </section>
  );
}

interface DiagramNodeProps {
  icon: React.ReactNode;
  title: string;
  desc: string;
  color: string;
  isSAP?: boolean;
}

function DiagramNode({ icon, title, desc, color, isSAP }: DiagramNodeProps) {
  return (
    <div className={`flex flex-col items-center w-48 bg-white dark:bg-slate-950 p-4 rounded-xl border-2 ${color} shadow-sm relative`}>
      {isSAP && <div className="absolute -top-3 right-2 bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded shadow">SAP</div>}
      <div className={`mb-3 text-slate-700 dark:text-slate-300`}>
        {icon}
      </div>
      <h4 className="font-bold text-sm text-center mb-1">{title}</h4>
      <p className="text-xs text-slate-500 text-center break-words w-full line-clamp-2">{desc}</p>
    </div>
  );
}

function DiagramArrow() {
  return (
    <div className="flex-shrink-0 text-slate-300 dark:text-slate-700">
      <ArrowRight size={24} />
    </div>
  );
}

interface SolutionBridgeProps {
  t: any;
}

function SolutionBridge({ t }: SolutionBridgeProps) {
  const tiles = t.solution.tiles as Array<{
    label: string;
    when: string;
    anchor: string;
    accent: 'enterprise' | 'growth' | 'neutral';
  }>;

  // Border + hover for the tile card (<a>)
  const accentBorderClasses = {
    enterprise: 'border-enterprise/40 hover:border-enterprise',
    growth:     'border-growth/40 hover:border-growth',
    neutral:    'border-slate-300 dark:border-slate-700 hover:border-slate-500',
  };
  // Text color for the tile label (<span>)
  const accentTextClasses = {
    enterprise: 'text-enterprise-dark dark:text-enterprise',
    growth:     'text-growth-dark dark:text-growth',
    neutral:    'text-blue-600 dark:text-blue-400',
  };

  return (
    <section className="py-16 bg-slate-50 dark:bg-slate-950 border-y border-slate-200 dark:border-slate-800">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <EyebrowLabel accent="neutral" centered className="mb-4">
          {t.solution.eyebrow}
        </EyebrowLabel>
        <h2 className="text-2xl md:text-3xl font-serif mb-10">{t.solution.headline}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {tiles.map((tile) => (
            <a
              key={tile.anchor}
              href={tile.anchor}
              className={`flex flex-col items-center p-6 rounded-xl border-2 bg-white dark:bg-slate-900 transition-colors group ${accentBorderClasses[tile.accent]}`}
            >
              <span className={`text-lg font-bold mb-2 ${accentTextClasses[tile.accent]}`}>
                {tile.label}
              </span>
              <span className="text-sm text-slate-500 dark:text-slate-400 text-center leading-relaxed">
                {tile.when}
              </span>
              <ArrowRight size={16} aria-hidden="true" className="mt-3 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

interface GrowthPlan {
  id: string;
  name: string;
  target: string;
  setup: string;
  monthly: string;
  features: string[];
  featured: boolean;
  caseStudy: { label: string; text: string } | null;
}

interface GrowthPlansGridProps {
  label: string;
  plans: GrowthPlan[];
  cta: string;
  ctaNote: string;
  footnote: string;
  featuredBadge: string;
  onCtaClick: () => void;
}

function GrowthPlansGrid({ label, plans, cta, ctaNote, footnote, featuredBadge, onCtaClick }: GrowthPlansGridProps) {
  return (
    <div className="flex flex-col gap-6">
      <span className="text-xs font-bold tracking-widest text-growth-dark dark:text-growth uppercase">
        {label}
      </span>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`flex flex-col p-6 rounded-2xl border transition-colors ${
              plan.featured
                ? 'border-2 border-growth bg-growth/5 dark:bg-growth/5'
                : plan.id === 'agricultura'
                ? 'border border-slate-200 dark:border-slate-800 border-l-4 border-l-green-300 dark:border-l-green-600 bg-white dark:bg-slate-900'
                : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900'
            }`}
          >
            {plan.featured && (
              <span className="text-xs font-bold tracking-widest text-growth uppercase mb-1">
                {featuredBadge}
              </span>
            )}
            <h3 className="text-xl font-bold mb-1">{plan.name}</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4 leading-relaxed">{plan.target}</p>
            <div className="mb-4">
              <span className="text-2xl font-black text-slate-400 dark:text-slate-500 italic">{plan.setup}</span>
              <p className="text-sm text-growth-dark dark:text-growth font-medium">{plan.monthly}</p>
            </div>
            <ul className="space-y-1.5 flex-1">
              {plan.features.map((f, i) => (
                <li key={i} className="flex items-start text-sm text-slate-600 dark:text-slate-400">
                  <TrendingUp size={14} className="mr-2 mt-0.5 shrink-0 text-growth-dark dark:text-growth" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            {plan.caseStudy && (
              <div className="mt-4 p-3 rounded-lg bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800/40">
                <p className="text-xs font-bold text-green-700 dark:text-green-400 uppercase tracking-wide mb-1">
                  {plan.caseStudy.label}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{plan.caseStudy.text}</p>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="text-center">
        <a
          href="#mapper"
          onClick={onCtaClick}
          className="inline-flex items-center px-7 py-3.5 rounded-lg font-semibold text-white bg-growth-dark hover:bg-growth transition-colors shadow-lg"
        >
          {cta}
          <ArrowRight size={18} className="ml-2" />
        </a>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{ctaNote}</p>
        <p className="mt-1 text-xs text-slate-400 dark:text-slate-600">{footnote}</p>
      </div>
    </div>
  );
}

interface ContactDialogProps {
  open: boolean;
  onClose: () => void;
  t: any;
  lang: Lang;
  branch: Branch;
}

function ContactDialog({ open, onClose, t, lang, branch }: ContactDialogProps) {
  const c = t.contactModal;
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', message: '', consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Reset transient state when the dialog closes so reopening starts fresh
  // (the component stays mounted; the parent only toggles `open`).
  useEffect(() => {
    if (!open) {
      setDone(false);
      setError(null);
      setSubmitting(false);
      setForm({ name: '', company: '', email: '', phone: '', message: '', consent: false });
    }
  }, [open]);

  if (!open) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.target;
    const value = target.type === 'checkbox' ? (target as HTMLInputElement).checked : target.value;
    setForm((f) => ({ ...f, [target.name]: value }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await postContact({
        contact: {
          name: form.name,
          company: form.company,
          email: form.email,
          phone: form.phone || null,
          consent: form.consent,
        },
        message: form.message || undefined,
        language: lang,
        branch,
      });
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : c.errorGeneric);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative w-full max-w-lg rounded-2xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 shadow-2xl p-6 md:p-8 animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label={c.close}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
        >
          <X size={20} />
        </button>

        {done ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 mx-auto bg-green-100 dark:bg-green-900/30 text-green-500 rounded-full flex items-center justify-center mb-5">
              <CheckCircle2 size={32} />
            </div>
            <h3 className="text-2xl font-bold mb-2">{c.successTitle}</h3>
            <p className="text-slate-600 dark:text-slate-400">{c.successMessage}</p>
          </div>
        ) : (
          <>
            <h3 className="text-2xl font-bold mb-1">{c.title}</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">{c.subtitle}</p>
            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm flex items-start">
                <AlertCircle size={16} className="mr-2 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-1">{c.name}</label>
                  <input type="text" name="name" required value={form.name} onChange={handleChange} className="p-2.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-1">{c.company}</label>
                  <input type="text" name="company" required value={form.company} onChange={handleChange} className="p-2.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-1">{c.email}</label>
                  <input type="email" name="email" required value={form.email} onChange={handleChange} className="p-2.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-1">{c.phone}</label>
                  <input type="tel" name="phone" value={form.phone} onChange={handleChange} className="p-2.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-1">{c.message}</label>
                <textarea name="message" rows={3} value={form.message} onChange={handleChange} placeholder={c.messagePh} className="p-2.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500 outline-none resize-none" />
              </div>
              <div className="flex items-start">
                <input type="checkbox" name="consent" required checked={form.consent} onChange={handleChange} className="mt-1 mr-3 rounded text-blue-600 focus:ring-blue-500" />
                <label className="text-sm text-slate-600 dark:text-slate-400">{c.consent}</label>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 mt-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white rounded-lg font-bold transition-all"
              >
                {submitting ? c.sending : c.submit}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
